import type { PendingConnectorWrite } from "@/lib/connectorPendingWrite";

/** A locator, never proof of success. The server must read its own receipt. */
export interface MirrorEmailReceiptReference {
  provider: "gmail" | "outlook_mail";
  capability: "draft" | "send" | "reply" | "organize";
  idempotency_key: string;
  action_id: string;
  userId: string;
  sessionId: string;
}

export interface MirrorEmailOrigin {
  userId: string;
  sessionId: string;
  actionId: string;
}

const object = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === "object" && !Array.isArray(value);

/** A recovered write can belong to a different conversation or proposal. */
export function mirrorEmailReceiptReference(
  entry: PendingConnectorWrite,
  origin: MirrorEmailOrigin,
): MirrorEmailReceiptReference | null {
  const body = entry.body;
  if (entry.group !== "email" || entry.status !== "succeeded" || entry.ownerId !== origin.userId ||
    entry.key !== origin.userId + ":email" ||
    !origin.userId || !origin.sessionId || !origin.actionId ||
    !["gmail", "outlook_mail"].includes(body.provider) ||
    !["draft", "send", "reply", "organize"].includes(body.action) ||
    body.idempotency_key !== "mirror_email_" + origin.actionId + "_" + body.action + "_0" ||
    !object(body.mirror_context) || body.mirror_context.session_id !== origin.sessionId ||
    body.mirror_context.action_id !== origin.actionId) return null;
  return {
    provider: body.provider as MirrorEmailReceiptReference["provider"],
    capability: body.action as MirrorEmailReceiptReference["capability"],
    idempotency_key: body.idempotency_key,
    action_id: origin.actionId,
    userId: origin.userId,
    sessionId: origin.sessionId,
  };
}

export function matchesMirrorEmailReceiptOrigin(
  value: unknown,
  origin: MirrorEmailOrigin,
): value is MirrorEmailReceiptReference {
  return object(value) && value.userId === origin.userId && value.sessionId === origin.sessionId &&
    value.action_id === origin.actionId && typeof value.provider === "string" &&
    ["gmail", "outlook_mail"].includes(value.provider) && typeof value.capability === "string" &&
    ["draft", "send", "reply", "organize"].includes(value.capability) &&
    value.idempotency_key === "mirror_email_" + origin.actionId + "_" + value.capability + "_0";
}
